import { Component, computed, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import * as fromAuth from '../../auth';
import * as fromLayout from '../../layout';
import { TopBarMobileResponsive } from '../components/top-bar.mobile.responsive';
import * as fromShared from '../shared';
import { teammatesFeature, TeammatesState } from './store';
import { TeammateAvatarComponent } from './teammate-avatar.component';
import { getTeammateAvatarFromParts } from './teammate-avatar.config';

@Component({
  imports: [
    TopBarMobileResponsive,
    TranslateModule,
    fromSeriousSystem.UseArrowIconDirective,
    TeammateAvatarComponent,
  ],
  template: `
    <div class="bg-gradient-e-vertical h-svh w-full flex flex-col relative">
      @if (isMobile()) {
        <squadbox-top-bar-mobile
          class="bg-shades-white"
          (chevronLeftButtonClicked)="handleChevronLeftButtonCTA()"
        >
        </squadbox-top-bar-mobile>
      }

      <div class="flex-1 overflow-y-auto hide-scrollbar">
        <div
          class="
            fade-in-up-subtle
            min-h-full
            flex flex-col items-center justify-center
            px-4 tablet-portrait:px-6
            py-16
          "
        >
          <div class="flex flex-col items-center gap-1 text-center">
            <p class="typo-h6 leading-8 text-neutral-500">
              {{ greeting() }}
            </p>
            <h1 class="typo-h4 leading-10 text-neutral-900">
              {{ 'TEAMMATE_LANDING.TITLE' | translate }}
            </h1>
          </div>

          <div
            class="
              mt-10
              grid w-full max-w-6xl
              grid-cols-[repeat(auto-fit,256px)]
              justify-center gap-4
            "
          >
            @for (teammate of teammates(); track teammate.id) {
              <button
                class="
                  group
                  relative
                  flex h-74 w-64 flex-col overflow-hidden
                  rounded-2xl border border-neutral-200 bg-shades-white
                  text-left shadow-sm transition-shadow duration-200
                  hover:shadow
                "
                (click)="handleClickTeammateCTA(teammate.slug)"
              >
                <squadbox-teammate-avatar
                  class="h-38"
                  [avatar]="getTeammateAvatarConfig(teammate)"
                />

                <div class="flex grow flex-col justify-between gap-4 p-6">
                  <div class="flex flex-col">
                    <p class="typo-p1 leading-6 font-semibold text-neutral-700">
                      {{ teammate.name }}
                    </p>
                    <p class="typo-p2 leading-6 text-neutral-500">
                      {{ teammate.title }}
                    </p>
                  </div>

                  <div class="flex items-end justify-between gap-4">
                    <div
                      class="
                        inline-flex items-center rounded border border-neutral-200
                        px-2 py-1 typo-caption leading-4 font-semibold
                        text-neutral-700
                      "
                    >
                      {{
                        'TEAMMATE_LANDING.EXPERTISE_COUNT'
                          | translate: { count: teammate.assistants.length }
                      }}
                    </div>

                    <div class="hidden tablet-landscape:flex card-arrow-action">
                      <svg sdUseArrowIcon="arrow-right"></svg>
                    </div>
                  </div>
                </div>
              </button>
            }
          </div>
        </div>
      </div>

      <div
        class="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-g-vertical"
      ></div>
    </div>
  `,
})
export class TeammatesLandingPage {
  private readonly layoutStore = inject(Store<fromLayout.LayoutState>);
  private readonly authStore = inject(Store<fromAuth.AuthState>);
  private readonly teammatesStore = inject<Store<TeammatesState>>(Store);
  private readonly routerService = inject(fromShared.RouterService);
  private readonly mobileNavigation = inject(
    fromShared.MobileNavigationService,
  );
  private readonly translateService = inject(TranslateService);

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  public readonly teammates = this.teammatesStore.selectSignal(
    teammatesFeature.selectAllWithAssistants,
  );
  private readonly currentUser = this.authStore.selectSignal(
    fromAuth.authFeature.selectUser,
  );

  public readonly greeting = computed(() => {
    const firstName = this.currentUser()?.firstName;

    return firstName
      ? (this.translateService.instant('TEAMMATE_LANDING.GREETING', {
          firstName,
        }) as string)
      : (this.translateService.instant(
          'TEAMMATE_LANDING.GREETING_FALLBACK',
        ) as string);
  });

  public async handleChevronLeftButtonCTA(): Promise<void> {
    await this.mobileNavigation.open();
  }

  public getTeammateAvatarConfig = getTeammateAvatarFromParts;

  public async handleClickTeammateCTA(slug: string): Promise<void> {
    await this.routerService.navigateToTeammate(slug);
  }
}
